import React, { useEffect, useMemo } from 'react';
import { useIsMobile } from '@/shared/components/DevMobileEmulator/DeviceModeContext';

export interface TabsProps {
  /** Array of tab labels */
  tabs: string[];
  /** Currently active tab label */
  activeTab: string;
  /** Callback when a tab is clicked */
  onTabChange: (tab: string) => void;

  /** Optional: sync tab selection into URL query param (enables middle-click open-in-new-tab). */
  urlKey?: string;

  /** Optional: enforce additional query params in the URL (useful for nested tabs). */
  urlParams?: Record<string, string>;
}

/**
 * A reusable tab navigation component. Renders horizontal tabs with hover and active styling.
 *
 * Desktop: standard horizontal bar (hidden below md breakpoint).
 * Device-emulator mode: compact scrollable pill bar — mobile-native UX.
 */
export default function Tabs({ tabs, activeTab, onTabChange, urlKey, urlParams }: TabsProps) {
  const deviceMobile = useIsMobile();

  const tabsKey = useMemo(() => (Array.isArray(tabs) ? tabs.join('|') : ''), [tabs]);

  const buildHref = useMemo(() => {
    if (!urlKey) return null;
    return (tab: string) => {
      try {
        const u = new URL(window.location.href);
        if (urlParams && typeof urlParams === 'object') {
          for (const [k, v] of Object.entries(urlParams)) {
            const kk = String(k ?? '').trim();
            if (!kk) continue;
            u.searchParams.set(kk, String(v ?? ''));
          }
        }
        u.searchParams.set(urlKey, tab);
        return `${u.pathname}${u.search}${u.hash}`;
      } catch {
        return '';
      }
    };
  }, [urlKey, urlParams]);

  useEffect(() => {
    if (!urlKey) return;
    if (!activeTab) return;
    if (!tabs.includes(activeTab)) return;

    try {
      const u = new URL(window.location.href);

      // If the URL already specifies a tab (and it's valid), prefer it.
      // This avoids overwriting deep-links on first mount (when activeTab is still the default).
      const fromUrl = u.searchParams.get(urlKey);
      if (fromUrl && tabs.includes(fromUrl) && fromUrl !== activeTab) {
        return;
      }

      if (urlParams && typeof urlParams === 'object') {
        for (const [k, v] of Object.entries(urlParams)) {
          const kk = String(k ?? '').trim();
          if (!kk) continue;
          u.searchParams.set(kk, String(v ?? ''));
        }
      }

      const curr = u.searchParams.get(urlKey);
      if (curr === activeTab) return;
      u.searchParams.set(urlKey, activeTab);
      const href = `${u.pathname}${u.search}${u.hash}`;
      window.history.replaceState({}, '', href);
    } catch {
      // ignore
    }
  }, [activeTab, tabs, urlKey, urlParams]);

  useEffect(() => {
    if (!urlKey) return;
    try {
      const u = new URL(window.location.href);
      const fromUrl = u.searchParams.get(urlKey);
      if (!fromUrl) return;
      if (!tabs.includes(fromUrl)) return;
      if (fromUrl === activeTab) return;
      onTabChange(fromUrl);
    } catch {
      // ignore
    }
  }, [activeTab, onTabChange, tabs, tabsKey, urlKey]);

  /* Device-emulator: hide tabs — BubbleNav handles all navigation */
  if (deviceMobile) return null;

  /* ─── Desktop: standard horizontal tab bar ───────────────────────── */
  return (
    <div className="hidden md:flex pac-glass-subtle border-b border-neon_purple-900/40">
      {tabs.map((tab) => {
        const isActive = tab === activeTab;

        const href = buildHref ? buildHref(tab) : '';
        return (
          buildHref ? (
            <a
              key={tab}
              href={href}
              onMouseDown={(e) => {
                // Middle mouse button (wheel click): open in new tab.
                if (e.button !== 1) return;
                e.preventDefault();
                try {
                  const abs = new URL(href, window.location.origin).toString();
                  window.open(abs, '_blank', 'noopener,noreferrer');
                } catch {
                  try {
                    if (href) window.open(href, '_blank', 'noopener,noreferrer');
                  } catch {
                    // ignore
                  }
                }
              }}
              onClick={(e) => {
                // Let browser handle modifiers (ctrl/cmd/shift) + non-left clicks.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onTabChange(tab);
                try {
                  if (href) window.history.replaceState({}, '', href);
                } catch {
                  // ignore
                }
              }}
              className={
                `pac-glass-tab px-4 py-2 text-sm flex-shrink-0
                ${isActive
                  ? 'pac-tab-active font-semibold text-neon_orange-300'
                  : 'text-neon_purple-400 hover:text-neon_purple-200'}
                `
              }
            >
              {tab}
            </a>
          ) : (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={
                `pac-glass-tab px-4 py-2 text-sm flex-shrink-0
                ${isActive
                  ? 'pac-tab-active font-semibold text-neon_orange-300'
                  : 'text-neon_purple-400 hover:text-neon_purple-200'}
                `
              }
            >
              {tab}
            </button>
          )
        );
      })}
    </div>
  );
}
